import React, { useMemo } from "react";

import { MetricsDashboardGraphTypeDisplayType } from "../../../__generated__/api";
import { Themes, useTheme } from "../../../contexts/Theme";
import Styles from "../Reports.module.scss";
import {
    CHART_COLORS,
    filter, formatDate, formatValueWithDisplay, getMetricValue, GraphProps,
    GraphSettingsProperties,
    isDimension,
} from "./base";
import Chart from "./Chart";
import ColumnProperties from "./Column";
import Filters from "./Filters";
import MetricProperties from "./MetricProperties";
import Title from "./Title";

const MAX_GROUPS = 8;
const MAX_GROUPS_V3 = 5;

export const COLORS_DARK = [
    "#768CFF",
    "#2842C7",
    "#FF833D",
    "#2D9D60",
    "#BBE3FA",
    "#B6C1FF",
    "#82D398",
    "#3FABE5",
    "#FEA97A",
    "#A1AFC0",
];

export const COLORS_LIGHT = [
    "#4E6AFF",
    "#102071",
    "#FF833D",
    "#3ABA6D",
    "#BBE3FA",
    "#B6C1FF",
    "#82D398",
    "#3FABE5",
    "#FEA97A",
    "#C8D2DE",
];

interface TooltipParams {
    name: string;
    percent: number;
    value: number
}

export function PieChartProperties(props: GraphSettingsProperties) {
    return (

        <div>

            <ColumnProperties
                field="dimensions"
                label="Dimensions"
                helpText="The list of fields which will be displayed in the pie chart.."
                canAddFormat
                {...props}
            />

            <MetricProperties {...props} />

            <Filters isAdmin={props.isAdmin} graph={props.graph} onChange={props.onChange} />

        </div>
    );
}

export default function PieChart(props: GraphProps) {
    const theme = useTheme();
    const options = useMemo(() => {
        const dimensions = props.graph.dimensions?.map(row => ({
            columnIndex: props.columnIndexMap[row.fieldId],
            dimension: row,
        })) ?? [];

        const dataRows = filter(props.data.rows, props.graph.filters, props.columnIndexMap);

        const rows = dataRows.groupBy(row => JSON.stringify(dimensions.map(dimension => {
            let value = row.values[dimension.columnIndex] as string;
            const date = new Date(value);
            if (dimension.dimension.format && !Number.isNaN(date)) {
                value = formatDate(date, dimension.dimension.format);
            }
            return value;
        })));

        const metric = props.graph.metrics?.firstElement;
        if (!metric) {
            return {
                title: "Invalid Metric",
            };
        }
        const metricIndex = props.columnIndexMap[metric.fieldId];
        const display = metric?.display ?? MetricsDashboardGraphTypeDisplayType.Number;
        const colors = theme.getTheme() === Themes.DARK ? COLORS_DARK : COLORS_LIGHT;

        const allGroups = Object.values(rows).mapFilter((group, index) => {
            const first = group.firstElement;

            const dimensionValues: (string | number | undefined)[] = dimensions.map(({ columnIndex, dimension }) => {
                const val = first.values[columnIndex] as string;
                let value = props.labelMap?.[val ?? ""];
                if (!value && val) {
                    const date = new Date(val);
                    if (dimension.format && !Number.isNaN(date)) {
                        value = formatDate(date, dimension.format);
                    }
                }
                return value ?? val;
            });

            const data = group.map(row => row.values[metricIndex]).filter(row => row != null && row !== "") as string[];

            return {
                value: getMetricValue(data, metric.aggregation),
                dataRow: data,
                name: dimensionValues.join(" - ") || "None",
                itemStyle: theme.isV3 ? {
                    color: colors[index % colors.length],
                } : undefined,
            };
        }).sortByDescending("value");

        const maxGroups = theme.isV3 ? MAX_GROUPS_V3 : MAX_GROUPS;
        const rowData = allGroups.limit(maxGroups);

        const rest = allGroups.skip(maxGroups);
        if (rest.isNotEmpty) {
            const data = rest.flatMap(row => row.dataRow);
            const index = rowData.length - 1;
            rowData.push({
                value: getMetricValue(data, metric.aggregation),
                dataRow: data,
                name: "Others",
                itemStyle: theme.isV3 ? {
                    color: colors[index % colors.length],
                } : undefined,
            });
        }
        const textColor = theme.isV3 ? CHART_COLORS.text() : undefined;

        return {
            tooltip: {
                trigger: "item",
                formatter: (params: TooltipParams) => {
                    const value = formatValueWithDisplay(params.value, display);
                    return `${params.name}: ${value} (${params.percent}%)`;
                },
            },
            // grid: {
            //     left: "50px",
            //     right: "50px",
            //     bottom: "50px",
            //     top: "10px",
            // },

            testId: props.testId,
            legend: theme.isV3 ? {
                show: true,
                type: theme.isV3 ? "scroll" : undefined,
                orient: "horizontal",
                width: "100%",
                bottom: "0px",
                padding: [20, 50, 5, 100],
                itemWidth: 10,
                itemHeight: 10,
                textStyle: {
                    color: textColor,
                },
                pageTextStyle: {
                    color: textColor,
                },
                pageIcons: {
                    horizontal: [
                        // eslint-disable-next-line max-len
                        "M10.8284 12.0007L15.7782 16.9504L14.364 18.3646L8 12.0007L14.364 5.63672L15.7782 7.05093L10.8284 12.0007Z",
                        // eslint-disable-next-line max-len
                        "M10.9763 10.0006L6.85156 5.87577L8.03007 4.69727L13.3334 10.0006L8.03007 15.3038L6.85156 14.1253L10.9763 10.0006Z",
                    ],
                },
                pageIconColor: textColor,
                pageIconInactiveColor: theme.isV3 ? CHART_COLORS.textDim() : undefined,
            } : undefined,
            series: [
                {
                    type: "pie",
                    radius: theme.isV3 ? ["65%", "85%"] : ["30%", "60%"],
                    data: rowData,
                    bottom: 30,
                    label: {
                        show: !theme.isV3,
                    },
                    padAngle: 2,
                    avoidLabelOverlap: true,
                    itemStyle: theme.isV3 ? {
                        borderRadius: 0,
                    } : {
                        borderRadius: 4,
                    },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowOffsetX: 0,
                            shadowColor: "rgba(0, 0, 0, 0.5)",
                        },
                    },
                },
            ],
        };
    }, [props.data, props.graph]);

    return (
        <div className={Styles["pie-chart"]}>
            <Title isAdmin={props.isAdmin} graph={props.graph} createMenu={props.createMenu} />

            <Chart options={options} ref={ref => ref?.rerender()} />
        </div>
    );
}
